/**
 * PayBills — tenants pay insurance + utility bills via Stripe.
 * Landlords add bills for tenants. Same route, role-aware UI.
 */
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader, Card, Button, Pill, Loader, Input, Textarea, Modal, EmptyState } from "@/components/ui/Primitives";
import { CreditCard, Plus, Zap, Wifi, Droplet, Flame, Shield, Trash2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}` });

const BILL_TYPES = [
  { value: "insurance",        label: "Tenant insurance",    icon: Shield  },
  { value: "utility_hydro",    label: "Hydro / Electricity", icon: Zap     },
  { value: "utility_gas",      label: "Gas",                 icon: Flame   },
  { value: "utility_water",    label: "Water",               icon: Droplet },
  { value: "utility_internet", label: "Internet",            icon: Wifi    },
  { value: "utility_other",    label: "Other utility",       icon: CreditCard },
];
const iconFor = (t) => (BILL_TYPES.find((b) => b.value === t)?.icon) || CreditCard;
const labelFor = (t) => (BILL_TYPES.find((b) => b.value === t)?.label) || t;
const TONE = { unpaid: "amber", processing: "blue", paid: "green", cancelled: "slate" };

export default function PayBills() {
  const { user } = useAuth();
  const isTenant = (user?.role || "").toLowerCase() === "tenant";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/bills`, { headers: auth() });
      const d = await r.json();
      setItems(d.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/bills/checkout/status/${sid}`, { headers: auth() });
        const d = await r.json();
        if (d.payment_status === "paid") toast.success("Bill paid — receipt saved.");
        else toast.info("Payment pending.");
      } catch { /* silent */ }
      const p = new URLSearchParams(params); p.delete("session_id"); setParams(p, { replace: true });
      load();
    })();
  }, [params, setParams, load]);

  async function pay(bill) {
    try {
      const r = await fetch(`${API}/api/bills/${bill.id}/checkout`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not start checkout");
      window.location.href = d.url;
    } catch (err) { toast.error(String(err.message)); }
  }

  async function cancelBill(bill) {
    if (!window.confirm(`Cancel this ${labelFor(bill.type).toLowerCase()} bill?`)) return;
    try {
      const r = await fetch(`${API}/api/bills/${bill.id}`, { method: "DELETE", headers: auth() });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || "Cancel failed"); }
      toast.success("Bill cancelled");
      load();
    } catch (err) { toast.error(String(err.message)); }
  }

  if (loading) return <Loader/>;
  return (
    <div data-testid="pay-bills-page">
      <PageHeader
        title="Bills · Insurance + utilities"
        subtitle={isTenant ? "Pay tenant insurance and utility bills right here. Every payment generates a receipt automatically." : "Add bills your tenants can pay in-app. Track status without email chases."}
      />
      {!isTenant && (
        <div className="mb-4 flex justify-end">
          <Button onClick={() => setCreateOpen(true)} data-testid="new-paybill-btn"><Plus className="w-4 h-4 inline mr-1"/>Add bill</Button>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState icon={CreditCard} title="No bills yet" description={isTenant ? "When your landlord adds a bill for you, it'll show up here." : "Add your first bill — insurance premium or a utility invoice."} />
      ) : (
        <div className="grid gap-3">
          {items.map((b) => {
            const Icon = iconFor(b.type);
            return (
              <Card key={b.id} className="p-4" data-testid={`paybill-${b.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="rounded-xl bg-slate-100 p-3 shrink-0"><Icon className="w-5 h-5 text-slate-700"/></div>
                    <div className="min-w-0">
                      <div className="font-display font-bold text-slate-900 truncate">{b.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{labelFor(b.type)}{b.provider ? ` · ${b.provider}` : ""}{b.due_date ? ` · due ${b.due_date}` : ""}</div>
                      {b.account_number && <div className="text-[11px] text-slate-400 font-mono mt-0.5">Acct {b.account_number}</div>}
                      <div className="mt-2"><Pill tone={TONE[b.status] || "slate"}>
                        {b.status === "paid" ? <CheckCircle2 className="w-3 h-3 inline mr-0.5"/> : b.status === "processing" ? <Clock className="w-3 h-3 inline mr-0.5"/> : null}
                        {b.status}
                      </Pill></div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className="font-display font-extrabold text-lg text-slate-900 tabular-nums">${((b.amount_cents || 0) / 100).toFixed(2)}</div>
                    {isTenant && b.status === "unpaid" && (
                      <Button onClick={() => pay(b)} className="!py-1.5 !px-3 text-xs" data-testid={`paybill-pay-${b.id}`}>
                        <CreditCard className="w-3 h-3 inline mr-1"/>Pay now
                      </Button>
                    )}
                    {isTenant && b.status === "processing" && (<div className="text-xs text-blue-700 font-bold">Processing…</div>)}
                    {!isTenant && b.status !== "paid" && b.status !== "cancelled" && (
                      <button onClick={() => cancelBill(b)} className="text-xs text-rose-700 font-bold hover:underline inline-flex items-center gap-1" data-testid={`paybill-cancel-${b.id}`}>
                        <Trash2 className="w-3 h-3"/> Cancel
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {createOpen && <NewPayBillModal onClose={() => setCreateOpen(false)} onCreated={(b) => { setCreateOpen(false); setItems((s) => [b, ...s]); }}/>}
    </div>
  );
}

function NewPayBillModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ type: "insurance", title: "", amount: "", tenant_email: "", provider: "", due_date: "", account_number: "", notes: "" });
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(form.amount || "0") * 100);
    if (!(amountCents > 0)) return toast.error("Enter a valid amount.");
    if (!form.tenant_email.trim()) return toast.error("Tenant email is required.");
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/bills`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({
          type: form.type, title: form.title.trim(), amount_cents: amountCents, currency: "cad",
          tenant_email: form.tenant_email.trim(), provider: form.provider.trim() || null,
          due_date: form.due_date.trim() || null, account_number: form.account_number.trim() || null,
          notes: form.notes.trim() || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not create");
      toast.success("Bill added — the tenant will see it in their portal.");
      onCreated(d);
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }
  return (
    <Modal open={true} onClose={onClose} title="Add a bill for a tenant" testid="new-paybill-modal">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Bill type</label>
          <select value={form.type} onChange={(e)=>setForm({...form,type:e.target.value})} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="paybill-type">
            {BILL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <Input label="Title / description" required value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} placeholder="Hydro · February" testid="paybill-title"/>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Amount (CAD)" required inputMode="decimal" type="number" step="0.01" min="0.50" value={form.amount} onChange={(e)=>setForm({...form,amount:e.target.value})} placeholder="82.50" testid="paybill-amount"/>
          <Input label="Due date (optional)" type="date" value={form.due_date} onChange={(e)=>setForm({...form,due_date:e.target.value})} testid="paybill-due-date"/>
        </div>
        <Input label="Tenant email" type="email" required value={form.tenant_email} onChange={(e)=>setForm({...form,tenant_email:e.target.value})} placeholder="tenant@example.com" testid="paybill-tenant-email"/>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Provider (optional)" value={form.provider} onChange={(e)=>setForm({...form,provider:e.target.value})} placeholder="Hydro One · Sonnet · Rogers" testid="paybill-provider"/>
          <Input label="Account # (optional)" value={form.account_number} onChange={(e)=>setForm({...form,account_number:e.target.value})} testid="paybill-account"/>
        </div>
        <Textarea label="Notes for the tenant (optional)" rows={2} value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} testid="paybill-notes"/>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="paybill-submit">{busy ? "Saving…" : "Add bill"}</Button>
        </div>
      </form>
    </Modal>
  );
}
